import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import React, { useEffect, useRef, useState } from 'react';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { useTranslation } from 'react-i18next';
import { OrderQueryProps, RefundOrderType } from '../../domain/report-provider/agentReportProvider';
import { usePageState } from '../../core/hooks/usePageState';
import { PageHeader } from 'antd';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import PaginationTransformer from '../../core/utils/paginationTransformer';
import { DateRangeStateType } from '../../core/utils/tableUtilType';
import { TableUtilColumn } from '../../core/utils/tableUtilColumn';
import { transfer2LocalTime } from '../../core/utils/timeFormat';
import { AdminListResponse } from '../../domain/models/adminListResponse';
import { useTxnType } from '../hook/orderStatus';
import ReportRefundCard from '../components/ReportRefundCard';

interface PageOp extends CoreProTableState<RefundOrderType> {
  totalRefund: {
    summeryUsdt:number,
    summeryBusd:number,
    summeryUsdc:number,
  };
}

export default function RefundOrders(props: {
  title: string;
  request: (param: OrderQueryProps) => Promise<AdminListResponse<any>>;
  resStatusMapping: () => any;
  reqStatusEnum: () => any;
}) {
  const actionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  const { t } = useTranslation();
  const pageOp = usePageState<PageOp>('PAGE_REMEMBER', {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1,
    },
    needRequest: true,
    totalRefund: {
      summeryUsdt:0,
      summeryBusd:0,
      summeryUsdc:0,
    },
  });
  const txnType = useTxnType();
  const dateRangeState = useState<DateRangeStateType>();
  const dateRangeColumn = TableUtilColumn.getDateRangeColumn({
    title: t('common.createdAt'),
    pageOp,
    state: dateRangeState,
  });
  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [searchFormRef, pageOp]);

  return (
    <>
      <PageHeader title={props.title} subTitle="" extra={<></>} />
      <CoreProTable<RefundOrderType>
        headerTitle={[
          // <Statistic
          //   key={'totalRefund'}
          //   precision={3}
          //   value={pageOp.get().totalRefund}
          //   title={t('agent.refund_total')}
          // />,
          <ReportRefundCard token={'USDT'} balance={pageOp.get().totalRefund.summeryUsdt} />,
          <ReportRefundCard token={'BUSD'} balance={pageOp.get().totalRefund.summeryBusd} />,
          <ReportRefundCard token={'USDC'} balance={pageOp.get().totalRefund.summeryUsdc} />
        ]
        }
        bordered
        rowKey={(record) => `${record.orderNo}-${record.secondAgentSystemId}`}
        updateTableState={(state) =>
          pageOp.update((v: any) => ({ ...v, ...state }))
        }
        onReset={() => {
          dateRangeColumn.reset();
          pageOp.update((v) => {
            v.needRequest = false;
            v.params = {};
            return v;
          });
        }}
        scroll={{ x: 'fixed' }}
        search={{
          layout: 'vertical',
          defaultCollapsed: false,
        }}
        tableState={pageOp.get()}
        requestData={async (params) => {
          console.log('params', params);
          const param = PaginationTransformer.paramTransfer({
            ...params,
            ...dateRangeColumn.getParamAndUpdatePage(),
          });
          const res = await props.request(param);
          const totalRefund = (res.data as any)?.summery ??
            {
              summeryUsdt:0,
              summeryBusd:0,
              summeryUsdc:0,
            };
          pageOp.set('totalRefund', totalRefund);
          return PaginationTransformer.responseTransfer(res);
        }}
        actionRef={actionRef as any}
        formRef={searchFormRef}
        columns={[
          {
            align: 'center',
            title: t(`payment.system_id`),
            dataIndex: 'systemId',
            width: 70,
            renderFormItem: (_: any, row, form: FormInstance<any>) => (
              <SystemIdSearchList
                onChange={(value) =>
                  form.setFieldsValue({ [_.dataIndex as string]: value })
                }
                systemId={pageOp.get().params.systemId}
              />
            ),
          },
          {
            align: 'center',
            hideInSearch: true,
            title: t('common.type'),
            valueEnum: txnType,
            dataIndex: 'txnType',
            initialValue: pageOp.get().params.txnType,
            width: 70,
          },
          {
            hideInTable: false,
            hideInSearch: true,
            align: 'center',
            title: t('payment.token'),
            dataIndex: 'token',
            width: 70,
          },
          {
            align: 'center',
            hideInSearch: false,
            hideInTable: false,
            ellipsis: true,
            title: t('payment.order_no'),
            dataIndex: 'orderNo',
            width: 100,
          },
          {
            align: 'right',
            hideInSearch: true,
            hideInTable: false,
            title: t('payment.order_amount'),
            dataIndex: 'orderAmount',
            width: 80,
          },
          {
            align: 'right',
            hideInSearch: true,
            hideInTable: false,
            title: t('payment.actual_amount'),
            dataIndex: 'actualAmount',
            width: 80,
          },
          {
            align: 'right',
            hideInSearch: true,
            hideInTable: false,
            title: t('agent.refund_amount'),
            dataIndex: 'refundAmount',
            width: 80,
          },
          {
            align: 'right',
            hideInSearch: true,
            hideInTable: false,
            title: t('agent.refund_fee_rate'),
            dataIndex: 'agentFeeRate',
            width: 80,
          },
          {
            align: 'center',
            hideInSearch: false,
            hideInTable: false,
            title: t('common.status'),
            dataIndex: 'status',
            valueEnum: props.reqStatusEnum(),
            render: (node, row) =>
              props.resStatusMapping()[row.status] ?? row.status,
            width: 70,
          },
          dateRangeColumn.column,
          {
            align: 'center',
            hideInSearch: true,
            hideInTable: false,
            width: 70,
            title: t('common.createdAt'),
            dataIndex: 'createdAt',
            render: (text, row: any) =>
              transfer2LocalTime(new Date(row.createdAt)),
          },
        ]}
      />
    </>
  );
}
